import Typography from '@material-ui/core/Typography';
import { FC, ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { ITeamMemberMapped } from '~/utils/clientAllocation';
import layoutStyles from '../../../shared/components/EditAllocationFormLayout/EditAllocationFormLayout.module.scss';
import useAllocationChangeTracking from '../../../shared/hooks/useAllocationChangeTracking';
import { IEditAllocationFormValues } from '../../../shared/model/types';
import useLocationGroupAllocationsUpdate from '../../hooks/updateAllocations/useLocationGroupAllocationsUpdate';
import AllocationLocationGroupsSelect from '../SelectFields/AllocationLocationGroupsSelect';
import AllocationLocationGroupRowItem from '../ClientsRowItem/AllocationLocationGroupRowItem';
import { useEditClientAllocationContext } from '../../context/EditClientAllocationContext';
import useAllocationConflictMessage from '../../../shared/hooks/useAllocationConflictMessage';

interface IProps {
  teamMemberIdx: number;
  teamMember: ITeamMemberMapped;
}

const AllocationLocationGroupsList: FC<IProps> = ({ teamMemberIdx, teamMember }): ReactElement => {
  const { locationGroups } = useEditClientAllocationContext();
  const { markChanged, markDeleted } = useAllocationChangeTracking<IEditAllocationFormValues>({
    changedField: 'changedLocationGroupAllocationsIds',
    deletedField: 'deletedLocationGroupAllocationsIds',
  });
  const { fields, updateLocationGroupAllocations, removeLocationGroupAt, removeClientFromGroup } =
    useLocationGroupAllocationsUpdate(teamMemberIdx);
  const { watch, setValue } = useFormContext<IEditAllocationFormValues>();

  const locationGroupAllocations = watch(
    `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations` as const,
  );
  const { activeClientIds, selectedLocationGroupClientIds } = useMemo(
    () =>
      locationGroupAllocations.reduce(
        (acc, lgAlloc) => {
          const removed = lgAlloc.removedClientIds ?? [];
          locationGroups
            .find(locationGroup => locationGroup.locationGroup.id === lgAlloc.locationGroupId)
            ?.clients.filter(client => {
              if (removed.includes(client.id)) return false;
              if (lgAlloc.allocation && !(client.id in lgAlloc.clientMemos)) return false;
              return true;
            })
            .forEach(client => {
              acc.activeClientIds.push(client.id);
              acc.selectedLocationGroupClientIds[client.id] = lgAlloc.locationGroupName;
            });
          return acc;
        },
        {
          activeClientIds: [] as number[],
          selectedLocationGroupClientIds: {} as Record<number, string>,
        },
      ),
    [locationGroupAllocations, locationGroups],
  );

  useEffect(() => {
    setValue(
      `teamMembersAllocations.${teamMemberIdx}.selectedLocationGroupClientIds` as const,
      selectedLocationGroupClientIds,
    );
  }, [selectedLocationGroupClientIds, setValue, teamMemberIdx]);

  const { locationGroupListConflict: conflictMessage } = useAllocationConflictMessage({
    teamMemberIdx,
    locationGroups,
  });

  const handleDeleteGroup = useCallback(
    (groupId: number, knownFieldIdx?: number) => {
      const fieldIdx =
        knownFieldIdx !== undefined
          ? knownFieldIdx
          : locationGroupAllocations.findIndex(f => f.locationGroupId === groupId);
      if (fieldIdx === -1) return;

      const field = locationGroupAllocations[fieldIdx];

      if (field.allocation?.id) {
        // For multi-team GU the same location group appears once per team allocation.
        // Check how many groups belong to THIS specific allocation (not the total count)
        // to decide whether to delete the allocation or just update it.
        const groupsForThisAllocation = locationGroupAllocations.filter(
          lg => lg.allocation?.id === field.allocation!.id,
        ).length;

        removeLocationGroupAt(fieldIdx);

        if (groupsForThisAllocation <= 1) {
          markDeleted(field.allocation.id);
          return;
        }
        markChanged(field.allocation.id);
      } else {
        removeLocationGroupAt(fieldIdx);
      }
    },
    [locationGroupAllocations, markChanged, markDeleted, removeLocationGroupAt],
  );

  const handleDeleteClient = useCallback(
    (groupId: number, clientId: number) => {
      // For multi-team GU the same location group creates multiple entries (one per team
      // allocation). Use clientMemos to find the entry that actually owns this client
      // instead of always taking the first matching entry via findIndex.
      let fieldIdx = locationGroupAllocations.findIndex(
        f => f.locationGroupId === groupId && clientId in (f.clientMemos ?? {}),
      );
      if (fieldIdx === -1) {
        fieldIdx = locationGroupAllocations.findIndex(f => f.locationGroupId === groupId);
      }
      if (fieldIdx === -1) return;

      const field = locationGroupAllocations[fieldIdx];
      const currentRemoved = field.removedClientIds ?? [];
      // Use the client count of this specific entry (from clientMemos) rather than the
      // global group total across all teams, so each team's allocation is tracked independently.
      const entryClientCount = Object.keys(field.clientMemos ?? {}).length;

      if (entryClientCount - (currentRemoved.length + 1) <= 0) {
        handleDeleteGroup(groupId, fieldIdx);
      } else {
        removeClientFromGroup(fieldIdx, clientId);
        if (field.allocation?.id) markChanged(field.allocation.id);
      }
    },
    [handleDeleteGroup, locationGroupAllocations, markChanged, removeClientFromGroup],
  );

  return (
    <>
      {conflictMessage && (
        <Typography variant="body2" className={layoutStyles.conflictAlert}>
          {conflictMessage}
        </Typography>
      )}
      <Typography variant="subtitle1" className={layoutStyles.assignedClientsSectionTitle}>
        Location Groups
      </Typography>

      <AllocationLocationGroupsSelect
        teamMemberIdx={teamMemberIdx}
        updateLocationGroupAllocations={updateLocationGroupAllocations}
        activeClientIds={activeClientIds}
        onDeleteClient={handleDeleteClient}
        onDeleteGroup={handleDeleteGroup}
      />

      {fields.map((field, fieldIdx) => (
        <AllocationLocationGroupRowItem
          key={field.id}
          field={field}
          fieldIdx={fieldIdx}
          teamMemberIdx={teamMemberIdx}
          memberId={teamMember.id}
          showGroupName={
            fields.findIndex(item => item.locationGroupId === field.locationGroupId) === fieldIdx
          }
          onRemoveClient={handleDeleteClient}
          onMarkChanged={markChanged}
        />
      ))}
    </>
  );
};

export default AllocationLocationGroupsList;
